import { ArrowDown, CalendarClock, Eye, FileLock2, HeartHandshake, MessageSquareText, NotebookPen, ShieldCheck, UserCheck, Users } from "lucide-react";
import { BrowserFrame, Cta, Eyebrow, H2, Lead, PhoneFrame, Section, Ticks } from "./parts";
import { TourButton } from "./Tour";

export const Hero = () => (
  <section className="bg-paper overflow-hidden" data-testid="mk-hero">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-20 grid lg:grid-cols-[1fr_1.15fr] gap-14 items-center">
      <div className="mk-rise">
        <Eyebrow>KNOWN Church Care</Eyebrow>
        <h1 className="font-display font-semibold text-ink tracking-[-0.035em] leading-[1.02] text-[46px] sm:text-[60px] lg:text-[68px] mt-6" data-testid="mk-hero-title">
          Keep people known.<br /><span className="text-[color:var(--moss)]">Keep care moving.</span>
        </h1>
        <Lead className="mt-7 max-w-[520px]">KNOWN helps your church see who needs care, know who owns the next step, and make sure follow-through actually happens.</Lead>
        <div className="flex flex-wrap gap-3 mt-9">
          <Cta testid="mk-hero-get-started" />
          <a href="#how" className="btn btn-lg btn-outline" data-testid="mk-hero-how">See How It Works</a>
        </div>
        <a href="#planning-center" className="mt-5 inline-flex items-center gap-3 rounded-full bg-white border border-line pl-4 pr-3 py-2 hover:border-[color:var(--moss)] transition-colors" data-testid="mk-hero-pco-badge">
          <span className="text-[14px] font-medium text-ink-soft">Integrates with</span>
          <span className="w-px h-5 bg-line" aria-hidden="true" />
          <img src="/marketing/planning-center.svg" alt="Planning Center" className="h-5 w-auto" width="134" height="20" />
        </a>
        <div><TourButton /></div>
      </div>
      <div className="relative mk-rise [animation-delay:120ms] pb-12 lg:pb-0">
        <BrowserFrame src="/marketing/mk_overview.jpeg" alt="KNOWN Pastor Overview showing Care Coverage, people needing attention and unassigned care needs" className="mr-10 sm:mr-20" />
        <PhoneFrame src="/marketing/mk_today_m.jpeg" alt="KNOWN Today on a phone" className="w-[30%] max-w-[180px] absolute -bottom-2 lg:-bottom-24 right-0" />
      </div>
    </div>
  </section>
);

const SCATTERED = ["Text messages", "Spreadsheets", "Prayer cards", "Personal notes", "Staff conversations", "Someone's memory"];
const SHARED = [[Eye, "Who needs care"], [UserCheck, "Who is responsible"], [NotebookPen, "What has already happened"], [CalendarClock, "What needs to happen next"]];

export const Problem = () => (
  <Section tone="stone" testid="mk-problem">
    <div className="grid lg:grid-cols-2 gap-14">
      <div>
        <Eyebrow>The problem</Eyebrow>
        <H2 className="mt-5 max-w-[520px]">Care shouldn't depend on somebody remembering.</H2>
        <Lead className="mt-6 max-w-[500px]">In most churches, pastoral care lives in a dozen places at once. When care is scattered, people can easily be missed. Not because no one cares, but because no one can see the whole picture.</Lead>
        <div className="flex flex-wrap gap-2 mt-8">
          {SCATTERED.map((s) => <span key={s} className="px-4 py-2 rounded-full border border-line bg-paper text-ink-soft text-[15px]">{s}</span>)}
        </div>
      </div>
      <div className="lg:pt-14">
        <p className="font-display font-semibold text-[22px] text-ink">KNOWN gives your church one shared place to see:</p>
        <div className="mt-6 border-t border-line">
          {SHARED.map(([Icon, t]) => (
            <div key={t} className="flex items-center gap-4 py-5 border-b border-line">
              <span className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] inline-flex items-center justify-center"><Icon size={20} className="text-[color:var(--deep)]" /></span>
              <span className="font-display font-semibold text-[19px] text-ink">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

const STEPS = [
  ["See who needs care", "Know who needs attention, who is outside the church's Care Rhythm, and where follow-up is overdue."],
  ["Know who owns it", "Assign people and needs to pastors, elders, care leaders, or ministry teams."],
  ["Keep the story together", "See care history, meaningful conversations, household context, and what someone has been walking through."],
  ["Know what happens next", "Schedule follow-ups so care does not disappear after one conversation."],
];

export const HowItWorks = () => (
  <Section id="how" testid="mk-how">
    <Eyebrow>How KNOWN works</Eyebrow>
    <H2 className="mt-5 max-w-[700px]">See the need. Share the care. Follow through.</H2>
    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 mt-14 items-start">
      <ol className="space-y-9">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="flex gap-5" data-testid={`mk-step-${i + 1}`}>
            <span className="font-display font-semibold text-[15px] w-10 h-10 shrink-0 rounded-full border border-[color:var(--deep)] text-[color:var(--deep)] inline-flex items-center justify-center">{i + 1}</span>
            <div><p className="font-display font-semibold text-[21px] text-ink">{t}</p><p className="text-ink-soft text-[17px] leading-relaxed mt-1.5">{d}</p></div>
          </li>
        ))}
      </ol>
      <div className="space-y-6">
        <BrowserFrame src="/marketing/mk_today.jpeg" alt="KNOWN Today list showing who needs attention" />
        <BrowserFrame src="/marketing/mk_person.jpeg" alt="A person's profile in KNOWN with care ownership, follow-ups and their Story" className="lg:ml-16" />
      </div>
    </div>
  </Section>
);

const SEES = ["Care Coverage", "People needing attention", "Unassigned care needs", "Overdue follow-ups", "Meaningful care connections", "Care-team workload", "Movement over time"];

export const PastorView = () => (
  <Section tone="stone" testid="mk-pastor">
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-14 items-center">
      <div>
        <Eyebrow>For pastors</Eyebrow>
        <H2 className="mt-5">See how care is moving across your church.</H2>
        <Lead className="mt-6">See where care is healthy, where gaps are forming, and where your team may need support.</Lead>
        <div className="mt-8"><Ticks items={SEES} /></div>
      </div>
      <BrowserFrame src="/marketing/mk_overview.jpeg" alt="KNOWN Pastor Overview" />
    </div>
  </Section>
);

const FLOW = ["Open KNOWN", "See who needs you", "Call or text", "Log the care", "Schedule the next step"];

export const LeaderView = () => (
  <Section tone="deep" testid="mk-leader">
    <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
      <div>
        <Eyebrow light>For care leaders</Eyebrow>
        <H2 light className="mt-5">Simple enough for the people actually doing the caring.</H2>
        <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="mk-leader-flow">
          {FLOW.map((f, i) => (
            <span key={f} className="flex items-center gap-2">
              <span className="px-4 py-2 rounded-full bg-[rgba(248,245,238,0.1)] text-[color:var(--cream)] text-[15px]">{f}</span>
              {i < FLOW.length - 1 && <span className="text-[color:var(--clay)]">→</span>}
            </span>
          ))}
        </div>
        <div className="mt-10"><Ticks light items={["A simple mobile experience", "Very few taps", "No complicated CRM", "No long forms", "No training manual required"]} /></div>
      </div>
      <div className="flex gap-5 justify-center items-start">
        <PhoneFrame src="/marketing/mk_today_m.jpeg" alt="Today on a phone" className="w-[46%] max-w-[250px] !bg-[#0A3127]" />
        <PhoneFrame src="/marketing/mk_log_m.jpeg" alt="Log Care on a phone" className="w-[46%] max-w-[250px] mt-14 !bg-[#0A3127]" />
      </div>
    </div>
  </Section>
);

const Box = ({ title, items, strong }) => (
  <div className={`rounded-[20px] px-8 py-7 text-center ${strong ? "bg-[color:var(--deep)] text-[color:var(--cream)]" : "bg-paper border border-line"}`}>
    <p className={`font-display font-bold text-[24px] tracking-[0.02em] ${strong ? "" : "text-ink"}`}>{title}</p>
    <p className={`mt-2 text-[16px] ${strong ? "text-[color:var(--stone)]" : "text-ink-soft"}`}>{items}</p>
  </div>
);

export const PlanningCenter = () => (
  <Section id="planning-center" tone="stone" testid="mk-pco">
    <div className="grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Eyebrow>Planning Center</Eyebrow>
        <H2 className="mt-5">Already using Planning Center? Keep it.</H2>
        <Lead className="mt-6">KNOWN isn't trying to replace Planning Center. Planning Center stays the source of truth for your people, households and lists.</Lead>
        <Lead className="mt-4">KNOWN becomes the dedicated care layer on top: care ownership, Care Groups, Care Rhythm, care needs, follow-ups, each person's Story, and pastoral oversight.</Lead>
      </div>
      <div className="max-w-[440px] w-full mx-auto" data-testid="mk-pco-diagram">
        <Box title="Planning Center" items="People · Households · Lists" />
        <div className="flex justify-center py-3"><span className="w-10 h-10 rounded-full bg-[color:var(--accent-soft)] inline-flex items-center justify-center"><ArrowDown size={20} className="text-[color:var(--accent-ink)]" /></span></div>
        <Box strong title="KNOWN" items="Care · Ownership · Follow-Up · Oversight" />
      </div>
    </div>
  </Section>
);

export const PeopleFirst = () => (
  <Section testid="mk-people-first">
    <div className="max-w-[860px]">
      <Eyebrow>People first</Eyebrow>
      <H2 className="mt-5">Your people are not tasks.</H2>
      <Lead className="mt-6">KNOWN was designed around people first. Pastoral care should not feel like closing tickets or managing cases. The person, their household, their story, what they are walking through, and the next step stay at the center.</Lead>
      <blockquote className="mt-12 pl-6 border-l-[3px] border-[color:var(--clay)] font-display font-semibold text-ink tracking-[-0.02em] text-[30px] sm:text-[40px] leading-[1.15]" data-testid="mk-quote">
        People deserve to be known, not just counted.
      </blockquote>
    </div>
  </Section>
);

const GROUPS = [["Care Circle 1", "Led by Monica Lindqvist", "Every 14 days"], ["Seniors", "Led by David Okafor", "Every 30 days"], ["New Members", "Led by Grace Holloway", "Every 14 days"], ["Homebound", "Led by Monica Lindqvist", "Every 7 days"], ["Young Adults", "Led by Daniel Reyes", "Every 45 days"]];

export const CareGroups = () => (
  <Section tone="stone" testid="mk-groups">
    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
      <div>
        <Eyebrow>Care Groups</Eyebrow>
        <H2 className="mt-5">Organize care the way your church actually works.</H2>
        <Lead className="mt-6">Create your own Care Groups, each with its own leaders and Care Rhythm. Already keep these as Planning Center Lists? Connect them, and membership stays in sync.</Lead>
      </div>
      <div className="rounded-[20px] bg-paper border border-line px-6 sm:px-8 py-3" aria-label="Example Care Groups">
        {GROUPS.map(([n, l, r]) => (
          <div key={n} className="flex items-center justify-between gap-4 py-4 border-b border-line last:border-0">
            <div><p className="font-display font-semibold text-[18px] text-ink">{n}</p><p className="text-ink-faint text-[14px]">{l}</p></div>
            <span className="status status-rhythm text-[14px] shrink-0">{r}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

const CONTROLS = [[Users, "Who sees people"], [HeartHandshake, "Who sees care information"], [FileLock2, "Who sees confidential pastoral notes"], [UserCheck, "Who can assign caregivers"], [ShieldCheck, "Who can manage the organization"]];

export const Privacy = () => (
  <Section testid="mk-privacy">
    <div className="grid lg:grid-cols-2 gap-14">
      <div>
        <Eyebrow>Privacy & permissions</Eyebrow>
        <H2 className="mt-5">Sensitive care deserves thoughtful access.</H2>
        <Lead className="mt-6">Your church decides who can see and do what. Confidential pastoral notes are permissioned separately from everyday care, and sensitive actions are recorded in an audit history.</Lead>
      </div>
      <div className="border-t border-line">
        {CONTROLS.map(([Icon, t]) => (
          <div key={t} className="flex items-center gap-4 py-4 border-b border-line">
            <Icon size={20} className="text-[color:var(--moss)] shrink-0" /><span className="text-[17px] text-ink">{t}</span>
          </div>
        ))}
        <p className="flex items-center gap-2 text-ink-faint text-[14px] mt-4"><MessageSquareText size={16} /> Confidential notes never appear in shared care history or emails.</p>
      </div>
    </div>
  </Section>
);
